import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Currency } from 'domains-billing-currency';
import { graphQLToCurrency } from 'domains-billing-web-mappers';

import {
  TaxDetails,
  convertTaxAmountTypeEnum,
} from 'lane-shared/domains/billingPayments/types';
import {
  DEFAULT_TAX_DETAILS,
  DEFAULT_ADMIN_FEE_TAX,
} from 'lane-shared/domains/billingPayments/constants';
import type { AdminFeeTax } from 'application-types';
import { convertToUUID } from 'uuid-encoding';

import {
  createBillingPaymentSettings,
  updateBillingPaymentSettings,
  getBillingPaymentSettings,
} from './helpers';

const DEFAULT_CURRENCY = Currency.USD;
const DEFAULT_DAYS = 30;

export type HookOptions = {
  channelId: string | undefined;
};

export function useBillingPaymentSettingsForChannel({
  channelId,
}: HookOptions) {
  const [error, setError] = useState<Error | null>(null);
  const [settingsUnchanged, setSettingsUnchanged] = useState<boolean>(true);

  const [taxDetails, setTaxDetails] = useState<TaxDetails>(DEFAULT_TAX_DETAILS);
  const [currency, setCurrency] = useState<Currency>(DEFAULT_CURRENCY);
  const [invoiceDueInDays, setInvoiceDueInDays] =
    useState<number>(DEFAULT_DAYS);

  const { t } = useTranslation();
  const defaultInvoiceDisplayName = t('abp.invoice.details.heading');

  const [invoiceDisplayName, setInvoiceDisplayName] = useState<string>(
    defaultInvoiceDisplayName
  );

  const [isInvoicingDisabled, setIsInvoicingDisabled] = useState(false);
  const [failureRefund, setFailureRefund] = useState<boolean>(false);
  const [cancellationRefund, setCancellationRefund] = useState<boolean>(false);
  const [isReportMarkupEnabled, setIsReportMarkupEnabled] =
    useState<boolean>(false);
  const [markupChargeCodeId, setMarkupChargeCodeId] = useState<string | null>(
    null
  );
  const [isReportAdminFeeEnabled, setIsReportAdminFeeEnabled] =
    useState<boolean>(false);
  const [adminFeeChargeCodeId, setAdminFeeChargeCodeId] = useState<
    string | null
  >(null);
  const [isAdminFeeTaxEnabled, setIsAdminFeeTaxEnabled] =
    useState<boolean>(false);
  const [adminFeeTax, setAdminFeeTax] = useState<AdminFeeTax | null>(null);

  const [isTaxPolicyLoaded, setIsTaxPolicyLoaded] = useState<boolean>(
    !channelId
  );

  function getSettings() {
    const settings: Record<string, unknown> = {
      taxDetails,
      currency,
      invoiceDueInDays,
      invoiceDisplayName,
      invoicingDisabled: isInvoicingDisabled,
      refund: {
        failureRefund,
        cancellationRefund,
      },
    };

    if (isReportMarkupEnabled && markupChargeCodeId) {
      settings.markup_charge_code_id = convertToUUID(markupChargeCodeId);
    }

    if (isReportAdminFeeEnabled && adminFeeChargeCodeId) {
      settings.admin_fee_charge_code_id = convertToUUID(adminFeeChargeCodeId);
    }

    if (isAdminFeeTaxEnabled && adminFeeTax) {
      settings.admin_fee_tax = {
        rate: adminFeeTax.rate,
        tax_amount_type: adminFeeTax.taxAmountType,
      };
    }

    return settings;
  }

  async function updatePaymentSettings(): Promise<boolean> {
    if (!channelId) {
      setError(new Error('abp.errors.generic.channelIdMissing'));

      return false;
    }

    let channelUUID;

    try {
      channelUUID = convertToUUID(channelId);
    } catch (err: any) {
      console.error(
        `BPError - updatePaymentSettings convertToUUID error: ${err}`
      );
      setError(new Error('abp.errors.generic.channelIdInvalid'));

      return false;
    }

    try {
      await updateBillingPaymentSettings(channelUUID, getSettings());

      setSettingsUnchanged(true);

      return true;
    } catch (err: any) {
      console.error(`BPError - updateBillingPaymentSettings error: ${err}`);
      setError(new Error(err.message));
    }

    return false;
  }

  function updateTaxDetails(value: TaxDetails) {
    setTaxDetails(value);
    setSettingsUnchanged(false);
  }

  function updateCurrency(value: Currency) {
    setCurrency(value);
    setSettingsUnchanged(false);
  }

  function updateInvoiceDueInDays(value: number | null) {
    setInvoiceDueInDays(value ?? DEFAULT_DAYS);
    setSettingsUnchanged(false);
  }

  function updateInvoiceDisplayName(value: string) {
    setInvoiceDisplayName(value);
    setSettingsUnchanged(false);
  }

  function updateIsInvoicingDisabled(value: boolean) {
    setIsInvoicingDisabled(value);
    setSettingsUnchanged(false);
  }

  function updateFailureRefund(value: boolean) {
    setFailureRefund(value);
    setSettingsUnchanged(false);
  }

  function updateCancellationRefund(value: boolean) {
    setCancellationRefund(value);
    setSettingsUnchanged(false);
  }

  function updateIsReportMarkupEnabled(value: boolean) {
    setIsReportMarkupEnabled(value);

    if (!value) {
      setMarkupChargeCodeId(null);
    }

    setSettingsUnchanged(false);
  }

  function updateMarkupChargeCodeId(value: string | null) {
    setMarkupChargeCodeId(value);
    setSettingsUnchanged(false);
  }

  function updateIsReportAdminFeeEnabled(value: boolean) {
    setIsReportAdminFeeEnabled(value);

    if (!value) {
      setAdminFeeChargeCodeId(null);
    }

    setSettingsUnchanged(false);
  }

  function updateAdminFeeChargeCodeId(value: string | null) {
    setAdminFeeChargeCodeId(value);
    setSettingsUnchanged(false);
  }

  function updateIsAdminFeeTaxEnabled(value: boolean) {
    setIsAdminFeeTaxEnabled(value);

    if (!value) {
      setAdminFeeTax(null);
    } else if (!adminFeeTax) {
      setAdminFeeTax(DEFAULT_ADMIN_FEE_TAX);
    }

    setSettingsUnchanged(false);
  }

  function updateAdminFeeTax(value: AdminFeeTax) {
    setAdminFeeTax(value);
    setSettingsUnchanged(false);
  }

  async function getPaymentSettings() {
    let channelUUID;

    try {
      channelUUID = convertToUUID(channelId);
    } catch (err: any) {
      console.error(`BPError - getPaymentSettings convertToUUID error: ${err}`);
      setError(new Error('abp.errors.generic.channelIdInvalid'));

      setIsTaxPolicyLoaded(true);

      return;
    }

    try {
      let channelSettings = await getBillingPaymentSettings(channelUUID);

      if (!channelSettings?.groupSettingId) {
        channelSettings = await createBillingPaymentSettings(
          channelUUID,
          getSettings()
        );
      }

      setTaxDetails({
        ...DEFAULT_TAX_DETAILS,
        ...channelSettings?.settings?.taxDetails,
        taxAmountType: convertTaxAmountTypeEnum(
          channelSettings?.settings?.taxDetails?.taxAmountType,
          DEFAULT_TAX_DETAILS.taxAmountType
        ),
      });

      const mappedCurrency = graphQLToCurrency(
        channelSettings?.settings?.currency
      );

      setCurrency(
        mappedCurrency === Currency.UNKNOWN ? DEFAULT_CURRENCY : mappedCurrency
      );
      setInvoiceDueInDays(channelSettings?.settings?.invoiceDueInDays);
      setInvoiceDisplayName(
        channelSettings?.settings?.invoiceDisplayName ||
          defaultInvoiceDisplayName
      );
      setIsInvoicingDisabled(channelSettings?.settings?.invoicingDisabled);

      setFailureRefund(
        channelSettings?.settings?.refund?.failureRefund || false
      );
      setCancellationRefund(
        channelSettings?.settings?.refund?.cancellationRefund || false
      );

      const markupChargeCodeIdRaw =
        channelSettings?.settings?.markup_charge_code_id ?? null;
      const loadedMarkupChargeCodeId =
        markupChargeCodeIdRaw && markupChargeCodeIdRaw !== ''
          ? markupChargeCodeIdRaw
          : null;

      setMarkupChargeCodeId(loadedMarkupChargeCodeId);
      setIsReportMarkupEnabled(!!loadedMarkupChargeCodeId);

      const adminFeeChargeCodeIdRaw =
        channelSettings?.settings?.admin_fee_charge_code_id ?? null;
      const loadedAdminFeeChargeCodeId =
        adminFeeChargeCodeIdRaw && adminFeeChargeCodeIdRaw !== ''
          ? adminFeeChargeCodeIdRaw
          : null;

      setAdminFeeChargeCodeId(loadedAdminFeeChargeCodeId);
      setIsReportAdminFeeEnabled(!!loadedAdminFeeChargeCodeId);

      const adminFeeTaxRaw = channelSettings?.settings?.admin_fee_tax ?? null;

      if (adminFeeTaxRaw) {
        setAdminFeeTax({
          rate: adminFeeTaxRaw.rate ?? DEFAULT_ADMIN_FEE_TAX.rate,
          taxAmountType: convertTaxAmountTypeEnum(
            adminFeeTaxRaw.tax_amount_type,
            DEFAULT_ADMIN_FEE_TAX.taxAmountType
          ) as AdminFeeTax['taxAmountType'],
        });
        setIsAdminFeeTaxEnabled(true);
      } else {
        setAdminFeeTax(null);
        setIsAdminFeeTaxEnabled(false);
      }
    } catch (err: any) {
      if (err instanceof Error) {
        console.error(`BPError - getBillingPaymentSettings error: ${err}`);
        setError(new Error(err.message));
      } else {
        console.error(
          `BPError - getBillingPaymentSettings non-standard error: ${{
            ...err,
          }}`
        );
        setError(new Error({ ...err }));
      }
    } finally {
      setIsTaxPolicyLoaded(true);
    }
  }

  useEffect(() => {
    if (channelId) {
      setIsTaxPolicyLoaded(false);
      getPaymentSettings();
    } else {
      setIsTaxPolicyLoaded(true);
    }
  }, [channelId]);

  return {
    paymentSettingsError: error,
    currency,
    updateCurrency,
    taxDetails,
    updateTaxDetails,
    invoiceDueInDays,
    updateInvoiceDueInDays,
    settingsUnchanged,
    updatePaymentSettings,
    invoiceDisplayName,
    updateInvoiceDisplayName,
    isInvoicingDisabled,
    updateIsInvoicingDisabled,
    failureRefund,
    cancellationRefund,
    updateFailureRefund,
    updateCancellationRefund,
    isReportMarkupEnabled,
    markupChargeCodeId,
    updateIsReportMarkupEnabled,
    updateMarkupChargeCodeId,
    isReportAdminFeeEnabled,
    adminFeeChargeCodeId,
    updateIsReportAdminFeeEnabled,
    updateAdminFeeChargeCodeId,
    isAdminFeeTaxEnabled,
    adminFeeTax,
    updateIsAdminFeeTaxEnabled,
    updateAdminFeeTax,
    isTaxPolicyLoaded,
  } as const;
}
